import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { AuthHandler } from '../auth/AuthHandler';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { MaybeWithLinks } from '../caremap/WithLinks';

export interface IConsentRecord {
    consentType: string;
    consentedAt: string;
    disclosureVersion: string;
    disclosureText: string;
    phone?: string;
}

export interface IUser {
    userId?: string;
    firstName?: string;
    lastName?: string;
    pictureUrl?: string;
    email?: string;
    phone?: string;
    workPhone?: string;
    address?: string;
    consents?: IConsentRecord[];
    updatedTime?: string;
    createdTime?: string; // note older users are missing this
};

export interface ILightUser {
    userId: string;
    firstName: string;
    lastName: string;
    pictureUrl: string;
};

/**
 * UserState is loaded in AppContainer before the app starts, and therefore will always be loaded.
 */
export class UserState extends State<MaybeWithLinks<IUser>> {

    isDirty = false;

    static inject = () => [
        APIClient,
        AuthHandler,
        BufferedEventBus
    ];

    constructor(
        private apiClient: APIClient,
        private authHandler: AuthHandler,
        private events: BufferedEventBus
    ) {
        super({});
    }

    load = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.user){
            return;
        }
        const user = await this.apiClient.get(entry.links.user);

        if (user) {
            this.setState(user);
        }
    };

    getName = (): string => {
        if (this.state.firstName && this.state.lastName){
            return `${this.state.firstName} ${this.state.lastName}`;
        }
        return this.state.firstName || '';
    };

    save = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.user){
            throw new Error('not supported');
        }

        await this.apiClient.put(entry.links.user, this.state);
        this.isDirty = false;
        this.setState({ });

        this.events.emit('UserState.saved');
    };

    onSaved = (listener: () => void) => {
        return this.events.on('UserState.saved', listener);
    };

    deleteAccount = async () => {
        const entry = await this.apiClient.entry();
        if (!entry.links.user){
            throw new Error('not supported');
        }

        this.events.emit('UserState.deleted');

        await this.apiClient.post(entry.links.deleteAccount, undefined);

        await this.authHandler.logout();
    };

    setFirstName = (firstName: string) => {
        this.isDirty = true;
        this.setState({ firstName });
    };

    setLastName = (lastName: string) => {
        this.isDirty = true;
        this.setState({ lastName });
    };

    setPhone = (phone: string) => {
        this.isDirty = true;
        this.setState({ phone });
    };

    setWorkPhone = (workPhone: string) => {
        this.isDirty = true;
        this.setState({ workPhone });
    };

    setAddress = (address: string) => {
        this.isDirty = true;
        this.setState({ address });
    };

    addConsent = (consent: IConsentRecord) => {
        this.isDirty = true;
        const existing = this.state.consents || [];
        this.setState({ consents: [...existing, consent] });
    };

    hasCompleteProfile = () => {
        return Boolean(this.getName() && this.state.address && this.state.phone);
    };

    hasSavedName = () => {
        return Boolean(this.state.firstName && !this.isDirty);
    };

}
